import {
  ResponseDataType,
  SelfRegisterCouponCreateSpace,
  SelfRegisterCouponImageDeleteSpace,
  SelfRegisterCouponImageUploadSpace,
} from 'src/types/api'
import React, { MouseEvent, useState } from 'react'
import useSelfRegisterForm from 'src/hooks/react-hook-form/useSelfRegisterForm.hook'
import { FormObjectType, SelfRegisterCouponFormItemsType } from 'src/types/react-hook-form'
import { FormProvider, SubmitHandler } from 'react-hook-form'
import {
  selfRegisterCouponCreateQueryOption,
  selfRegisterCouponImageDeleteQueryOption,
  selfRegisterCouponImageUploadQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import SelfRegCouponCreatePageView from 'src/pages/SelfRegCouponPage/SelfRegCouponCreatePage/view'
import { ERRCODE_SUCCESS } from 'src/service/constant/errcodes'
import usePageNav from 'src/hooks/usePageNav.hook'
import { useToast } from 'src/provider/ToastContextProvider'
import RenderIf from 'src/components/common/RenderIf'
import { Loading } from 'src/components/common/Loading'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { handleJsonParse } from 'src/utils'
import { Bridge } from 'src/utils/bridge'

export interface SelfRegCouponCreatePageProps {
  registerAttribute: Record<keyof SelfRegisterCouponFormItemsType, Partial<FormObjectType>>
  selfRegCouponCreateHandler: SubmitHandler<SelfRegisterCouponFormItemsType>
  selfRegImageDeleteHandler: (event: MouseEvent<HTMLButtonElement>) => void
  requestSelectImageHandler: () => void
}

const SelfRegCouponCreatePage = () => {
  const { mutateAsync: selfImgUploadMutate, isPending: isSelfImgUploadPending } = useCustomMutation<
    SelfRegisterCouponImageUploadSpace.Request,
    SelfRegisterCouponImageUploadSpace.Response
  >(selfRegisterCouponImageUploadQueryOption())

  const { mutateAsync: selfImgDeleteMutate, isPending: isSelfImgDeletePending } = useCustomMutation<
    SelfRegisterCouponImageDeleteSpace.Request,
    SelfRegisterCouponImageDeleteSpace.Response
  >(selfRegisterCouponImageDeleteQueryOption())

  const { mutateAsync: selfCreateMutate, isPending: isSelfCreatePending } = useCustomMutation<
    SelfRegisterCouponCreateSpace.Request,
    SelfRegisterCouponCreateSpace.Response
  >(selfRegisterCouponCreateQueryOption())

  const { methods, registerAttribute } = useSelfRegisterForm({
    selfImgUploadMutate,
    selfImgDeleteMutate,
  })

  const { getValues, setValue, trigger, resetField, formState } = methods
  const { defaultValues } = formState

  const { web } = useChannel()
  const { selfRegisterListPageNav } = usePageNav()
  const { openToast } = useToast()
  const [isLoadingImgUpload, setIsLoadingImgUpload] = useState(false)

  const selfRegImageDeleteHandler = (event: MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault()
    selfImgDeleteMutate({ CouponImageURL: getValues('couponImage') }).then(({ Header }) => {
      if (Header.ErrCode === ERRCODE_SUCCESS) {
        resetField('couponImage')
        resetField('barcode')
        resetField('validDate')
      }
    })
  }

  const selfRegCouponCreateHandler: SubmitHandler<SelfRegisterCouponFormItemsType> = (data) => {
    const { couponImage, couponName, brandName, memo, barcode, useFlag, validDate } = data
    const submitData = {
      CouponImageURL: couponImage === defaultValues?.couponImage ? '' : couponImage,
      CouponName: couponName,
      BrandName: brandName,
      Barcode: barcode,
      Memo: memo,
      ExpireDT: validDate,
      useFlag: Number(useFlag),
    }
    selfCreateMutate(submitData).then(({ Header, Body }) => {
      if (Header.ErrCode === ERRCODE_SUCCESS) {
        openToast({ contents: '쿠폰이 등록되었습니다.' })
        selfRegisterListPageNav(true)
      }
    })
  }

  const requestSelectImageHandler = () => {
    if (web) {
      return
    }
    setIsLoadingImgUpload(true)
    Bridge.getUploadImage.req()

    Bridge.getUploadImage.res((result) => {
      const errorToText = {
        error_type: 'pdf 또는 이미지 파일만 업로드 가능합니다.',
        error_size: '5MB 이하 파일만 업로드 가능합니다.',
        error_default: '파일을 찾을 수 없습니다.',
      }
      const isKeyInErrorToText = (result: string): result is keyof typeof errorToText => {
        return result in errorToText
      }

      setIsLoadingImgUpload(false)

      if (isKeyInErrorToText(result)) {
        openToast({
          contents:
            errorToText[result as keyof typeof errorToText] || '알 수 없는 오류가 발생했습니다.',
        })
      } else {
        console.warn(result)

        const { Body, Header } = handleJsonParse(
          result,
        ) as ResponseDataType<SelfRegisterCouponImageUploadSpace.Response>

        if (Header.ErrCode === ERRCODE_SUCCESS) {
          const { CouponImageURL, ExpireDT, Barcode } = Body!
          setValue('barcode', Barcode)
          setValue('validDate', ExpireDT)
          setValue('couponImage', CouponImageURL)
          trigger?.()
        } else {
          console.warn(Header.ErrCode)
          openToast({ contents: Header.ErrMsg })
        }
      }
    })
  }

  const props: SelfRegCouponCreatePageProps = {
    selfRegImageDeleteHandler,
    registerAttribute,
    selfRegCouponCreateHandler,
    requestSelectImageHandler,
  }

  return (
    <FormProvider {...methods}>
      <RenderIf when={isLoadingImgUpload || (isSelfImgUploadPending && web)}>
        <Loading.OCR />
      </RenderIf>
      <RenderIf when={isSelfImgDeletePending || isSelfCreatePending}>
        <Loading.Default />
      </RenderIf>
      <SelfRegCouponCreatePageView {...props} />
    </FormProvider>
  )
}
export default SelfRegCouponCreatePage
